<template>
  <BaseVeeForm
    :action="submitForm"
    ensure-error-keys-camel-case
    v-slot="{ isPending, invalid }"
    class="space-y-8"
  >
    <div class="space-y-12">
      <ValidationProvider rules="required" name="name" slim vid="name">
        <template #default="{ errors }">
          <BaseFormGroup>
            <BaseFormLabel> Name </BaseFormLabel>
            <BaseFormInlineMessage info>
              A name to easily distinguish the service.
            </BaseFormInlineMessage>
            <BaseFormInput
              type="text"
              v-model="formData.config.name"
              readonly
              class="w-2/3"
              @focus="$event.target.removeAttribute('readonly')"
            />
            <BaseFormInlineMessage v-if="errors.length" danger>
              {{ errors[0] }}
            </BaseFormInlineMessage>
          </BaseFormGroup>
        </template>
      </ValidationProvider>

      <div class="space-y-8">
        <div>
          <h3 class="text-lg leading-6 font-medium text-gray-900">General Settings</h3>
        </div>

        <div class="space-y-4">
          <div class="px-2 space-y-4">
            <ValidationProvider
              :rules="{ required: { allowFalse: true } }"
              name="personal"
              slim
              vid="personalThirdPartyService"
            >
              <template #default="{ errors }">
                <BaseFormGroup>
                  <BaseFormCheckbox
                    label="Personal service"
                    v-model="formData.personalThirdPartyService"
                  />
                  <BaseFormInlineMessage v-if="errors.length" danger>
                    {{ errors[0] }}
                  </BaseFormInlineMessage>
                </BaseFormGroup>
              </template>
            </ValidationProvider>

            <ValidationProvider name="signature" slim vid="signature">
              <template #default="{ errors }">
                <BaseFormGroup>
                  <BaseFormLabel> Signature </BaseFormLabel>
                  <BaseFormTextarea v-model="formData.config.signature" rows="4" />
                  <BaseFormInlineMessage v-if="errors.length" danger>
                    {{ errors[0] }}
                  </BaseFormInlineMessage>
                </BaseFormGroup>
              </template>
            </ValidationProvider>
          </div>
        </div>
      </div>

      <!-- Smtp Settings (Outgoing) -->
      <div class="space-y-8">
        <div>
          <h3 class="text-lg leading-6 font-medium text-gray-900">SMTP Settings (Outgoing)</h3>
        </div>

        <div class="space-y-4">
          <div class="flex -mx-2">
            <div class="w-2/3 px-2">
              <ValidationProvider rules="required" name="smtp host" slim vid="smtpHost">
                <template #default="{ errors }">
                  <BaseFormGroup>
                    <BaseFormLabel> Host </BaseFormLabel>
                    <BaseFormInput
                      type="text"
                      v-model="formData.config.smtpHost"
                      readonly
                      @focus="$event.target.removeAttribute('readonly')"
                    />
                    <BaseFormInlineMessage v-if="errors.length" danger>
                      {{ errors[0] }}
                    </BaseFormInlineMessage>
                  </BaseFormGroup>
                </template>
              </ValidationProvider>
            </div>
            <div class="w-1/3 px-2">
              <ValidationProvider rules="required" name="smtp port" slim vid="smtpPort">
                <template #default="{ errors }">
                  <BaseFormGroup>
                    <BaseFormLabel> Port </BaseFormLabel>
                    <BaseFormInput
                      type="text"
                      v-model="formData.config.smtpPort"
                      readonly
                      class="w-2/3"
                      @focus="$event.target.removeAttribute('readonly')"
                    />
                    <BaseFormInlineMessage v-if="errors.length" danger>
                      {{ errors[0] }}
                    </BaseFormInlineMessage>
                  </BaseFormGroup>
                </template>
              </ValidationProvider>
            </div>
          </div>

          <ValidationProvider rules="required" name="smtp encryption" slim vid="smtpEncryption">
            <template #default="{ errors }">
              <BaseFormGroup>
                <BaseFormLabel> Encryption </BaseFormLabel>
                <div>
                  <BaseFormSelect
                    v-model="formData.config.smtpEncryption"
                    :options="[
                      { value: 'ssl', label: 'SSL' },
                      { value: 'tls', label: 'TLS' },
                    ]"
                    required
                    :invalid="errors.length > 0"
                  />
                </div>
                <BaseFormInlineMessage v-if="errors.length" danger>
                  {{ errors[0] }}
                </BaseFormInlineMessage>
              </BaseFormGroup>
            </template>
          </ValidationProvider>

          <ValidationProvider rules="required" name="smtp username" slim vid="smtpUsername">
            <template #default="{ errors }">
              <BaseFormGroup>
                <BaseFormLabel> Username </BaseFormLabel>
                <BaseFormInput
                  type="text"
                  v-model="formData.config.smtpUsername"
                  readonly
                  class="w-2/3"
                  @focus="$event.target.removeAttribute('readonly')"
                />
                <BaseFormInlineMessage v-if="errors.length" danger>
                  {{ errors[0] }}
                </BaseFormInlineMessage>
              </BaseFormGroup>
            </template>
          </ValidationProvider>

          <ValidationProvider rules="required" name="smtp password" slim vid="smtpPassword">
            <template #default="{ errors }">
              <BaseFormGroup>
                <BaseFormLabel> Password </BaseFormLabel>
                <div class="flex space-x-2">
                  <BaseFormInput
                    :type="showSmtpPassword ? 'text' : 'password'"
                    v-model="formData.config.smtpPassword"
                    class="w-2/3"
                    @focus="$event.target.removeAttribute('readonly')"
                  />
                  <BaseButton
                    type="button"
                    primary
                    outline
                    @click="setShowSmtpPassword(!showSmtpPassword)"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="icon icon-tabler icon-tabler-eye"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      stroke-width="2"
                      stroke="currentColor"
                      fill="none"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                      <template v-if="showSmtpPassword">
                        <line x1="3" y1="3" x2="21" y2="21" />
                        <path d="M10.584 10.587a2 2 0 0 0 2.828 2.83" />
                        <path
                          d="M9.363 5.365a9.466 9.466 0 0 1 2.637 -.365c4
                        0 7.333 2.333 10 7c-.778 1.361 -1.612 2.524 -2.503
                        3.488m-2.14 1.861c-1.631 1.1 -3.415 1.651 -5.357
                        1.651c-4 0 -7.333 -2.333 -10 -7c1.369 -2.395 2.913 -4.175 4.632 -5.341"
                        />
                      </template>
                      <template v-else>
                        <circle cx="12" cy="12" r="2" />
                        <path
                          d="M22 12c-2.667 4.667 -6 7 -10 7s-7.333
                        -2.333 -10 -7c2.667 -4.667 6 -7 10 -7s7.333 2.333 10 7"
                        />
                      </template>
                    </svg>
                  </BaseButton>
                </div>
                <BaseFormInlineMessage v-if="errors.length" danger>
                  {{ errors[0] }}
                </BaseFormInlineMessage>
              </BaseFormGroup>
            </template>
          </ValidationProvider>

          <ValidationProvider rules="required" name="from email" slim vid="fromEmail">
            <template #default="{ errors }">
              <BaseFormGroup>
                <BaseFormLabel> From email </BaseFormLabel>
                <BaseFormInput type="email" v-model="formData.config.fromEmail" class="w-2/3" />
                <BaseFormInlineMessage v-if="errors.length" danger>
                  {{ errors[0] }}
                </BaseFormInlineMessage>
              </BaseFormGroup>
            </template>
          </ValidationProvider>

          <ValidationProvider rules="required" name="from name" slim vid="fromName">
            <template #default="{ errors }">
              <BaseFormGroup>
                <BaseFormLabel> From name </BaseFormLabel>
                <BaseFormInput type="text" v-model="formData.config.fromName" class="w-2/3" />
                <BaseFormInlineMessage v-if="errors.length" danger>
                  {{ errors[0] }}
                </BaseFormInlineMessage>
              </BaseFormGroup>
            </template>
          </ValidationProvider>
        </div>
      </div>

      <!-- Imap Settings (Incoming) -->
      <div class="space-y-8">
        <div>
          <h3 class="text-lg leading-6 font-medium text-gray-900">Imap Settings (Incoming)</h3>
        </div>

        <div class="space-y-4">
          <div class="flex -mx-2">
            <div class="w-2/3 px-2">
              <ValidationProvider rules="required" name="imap host" slim vid="imapHost">
                <template #default="{ errors }">
                  <BaseFormGroup>
                    <BaseFormLabel> Host </BaseFormLabel>
                    <BaseFormInput
                      type="text"
                      v-model="formData.config.imapHost"
                      readonly
                      @focus="$event.target.removeAttribute('readonly')"
                    />
                    <BaseFormInlineMessage v-if="errors.length" danger>
                      {{ errors[0] }}
                    </BaseFormInlineMessage>
                  </BaseFormGroup>
                </template>
              </ValidationProvider>
            </div>
            <div class="w-1/3 px-2">
              <ValidationProvider rules="required" name="imap port" slim vid="imapPort">
                <template #default="{ errors }">
                  <BaseFormGroup>
                    <BaseFormLabel> Port </BaseFormLabel>
                    <BaseFormInput
                      type="text"
                      v-model="formData.config.imapPort"
                      readonly
                      class="w-2/3"
                      @focus="$event.target.removeAttribute('readonly')"
                    />
                    <BaseFormInlineMessage v-if="errors.length" danger>
                      {{ errors[0] }}
                    </BaseFormInlineMessage>
                  </BaseFormGroup>
                </template>
              </ValidationProvider>
            </div>
          </div>

          <ValidationProvider rules="required" name="imap username" slim vid="imapUsername">
            <template #default="{ errors }">
              <BaseFormGroup>
                <BaseFormLabel> Username </BaseFormLabel>
                <BaseFormInput
                  type="text"
                  v-model="formData.config.imapUsername"
                  readonly
                  class="w-2/3"
                  @focus="$event.target.removeAttribute('readonly')"
                />
                <BaseFormInlineMessage v-if="errors.length" danger>
                  {{ errors[0] }}
                </BaseFormInlineMessage>
              </BaseFormGroup>
            </template>
          </ValidationProvider>

          <ValidationProvider rules="required" name="imap password" slim vid="imapPassword">
            <template #default="{ errors }">
              <BaseFormGroup>
                <BaseFormLabel> Password </BaseFormLabel>
                <div class="flex space-x-2">
                  <BaseFormInput
                    :type="showImapPassword ? 'text' : 'password'"
                    v-model="formData.config.imapPassword"
                    class="w-2/3"
                    @focus="$event.target.removeAttribute('readonly')"
                  />
                  <BaseButton
                    type="button"
                    primary
                    outline
                    @click="setShowImapPassword(!showImapPassword)"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="icon icon-tabler icon-tabler-eye"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      stroke-width="2"
                      stroke="currentColor"
                      fill="none"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                      <template v-if="showSmtpPassword">
                        <line x1="3" y1="3" x2="21" y2="21" />
                        <path d="M10.584 10.587a2 2 0 0 0 2.828 2.83" />
                        <path
                          d="M9.363 5.365a9.466 9.466 0 0 1 2.637 -.365c4
                        0 7.333 2.333 10 7c-.778 1.361 -1.612 2.524 -2.503
                        3.488m-2.14 1.861c-1.631 1.1 -3.415 1.651 -5.357
                        1.651c-4 0 -7.333 -2.333 -10 -7c1.369 -2.395 2.913 -4.175 4.632 -5.341"
                        />
                      </template>
                      <template v-else>
                        <circle cx="12" cy="12" r="2" />
                        <path
                          d="M22 12c-2.667 4.667 -6 7 -10 7s-7.333
                        -2.333 -10 -7c2.667 -4.667 6 -7 10 -7s7.333 2.333 10 7"
                        />
                      </template>
                    </svg>
                  </BaseButton>
                </div>
                <BaseFormInlineMessage v-if="errors.length" danger>
                  {{ errors[0] }}
                </BaseFormInlineMessage>
              </BaseFormGroup>
            </template>
          </ValidationProvider>
        </div>
      </div>

      <!-- Send Limits -->
      <div class="space-y-8">
        <div>
          <h3 class="text-lg leading-6 font-medium text-gray-900">Send Limits</h3>
        </div>

        <div class="space-y-4">
          <BaseFormTextFieldWithValidator
            v-model="formData.config.sendLimit"
            v-bind="sendLimitFieldProps"
            :disabled="isPending"
          />
          <BaseFormTextFieldWithValidator
            v-model="formData.config.sendInterval"
            v-bind="sendIntervalFieldProps"
            :disabled="isPending"
          />
          <BaseFormSelectFieldWithValidator
            v-model="formData.config.sendPeriod"
            v-bind="sendPeriodFieldProps"
            :disabled="isPending"
          />
        </div>
      </div>
    </div>
    <div class="space-x-2">
      <BaseButton primary :disabled="isPending || invalid"> Submit </BaseButton>

      <BaseButton type="button" ghost :disabled="isPending" @click="$emit('cancel')">
        Cancel
      </BaseButton>
    </div>
  </BaseVeeForm>
</template>

<script>
import { pick, merge } from 'lodash-es';
import BaseVeeForm from 'Components/BaseVeeForm.vue';

const defaultFormData = {
  name: '',
  smtpHost: '',
  smtpPort: '465',
  smtpEncryption: 'ssl',
  smtpUsername: '',
  smtpPassword: '',
  fromEmail: '',
  fromName: '',
  imapHost: '',
  imapPort: '993',
  imapEncryption: '',
  imapUsername: '',
  signature: '',
  sendLimit: 3,
  sendInterval: 1,
  sendPeriod: 'minute',
};

const getDefaultConfigData = (config = {}) =>
  merge(
    { ...defaultFormData },
    pick(config, [
      'name',
      'fromEmail',
      'fromName',
      'smtpHost',
      'smtpPort',
      'smtpUsername',
      'smtpPassword',
      'smtpEncryption',
      'imapHost',
      'imapPort',
      'imapUsername',
      'imapPassword',
      'signature',
      'sendLimit',
      'sendInterval',
      'sendPeriod',
    ])
  );

export default {
  components: { BaseVeeForm },
  props: {
    defaultData: {
      type: Object,
      default: () => ({}),
    },
    action: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      showSmtpPassword: false,
      showImapPassword: false,
      formData: {},
    };
  },
  computed: {
    sendLimitFieldProps() {
      return {
        validator: {
          name: 'send limit',
          vid: 'send_limit',
          rules: { required: true, min: 1 },
        },
        field: {
          inputType: 'number',
          id: 'send-limit',
          label: 'Send Limit (Amount of emails)',
          inputClasses: 'w-48',
        },
      };
    },
    sendIntervalFieldProps() {
      return {
        validator: {
          name: 'send interval',
          vid: 'send_interval',
          rules: { required: true, min: 1 },
        },
        field: {
          inputType: 'number',
          id: 'send-interval',
          label: 'Send Interval',
          inputClasses: 'w-48',
        },
      };
    },
    sendPeriodFieldProps() {
      return {
        validator: {
          name: 'send period',
          vid: 'send_period',
          rules: { required: true },
        },
        field: {
          options: [
            { value: 'minute', label: 'Minute' },
            { value: 'hour', label: 'Hour' },
            { value: 'day', label: 'Day' },
          ],
          id: 'send-period',
          label: 'Send Period',
        },
      };
    },
  },
  created() {
    this.setDefaultData();
  },
  destroyed() {
    this.formData = {};
  },
  methods: {
    setShowSmtpPassword(value) {
      this.showSmtpPassword = Boolean(value);
    },
    setShowImapPassword(value) {
      this.showImapPassword = Boolean(value);
    },
    setDefaultData() {
      this.formData = {
        personalThirdPartyService: this.defaultData?.personalThirdPartyService || false,
        config: getDefaultConfigData(this.defaultData?.config || {}),
      };
    },
    async submitForm() {
      try {
        await this.action(this.formData);
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
  },
};
</script>
